import {
    ArticleApiClient,
    ArticleStatus,
    ArticleType,
    CreateArticleDto,
    CreateArticleTemplateDto,
    GetArticleCategoryDto,
    ICreateArticleDto,
    ICreateArticleTemplateDto,
    IPublishArticleDto,
    IRedraftArticleDto,
    IScheduleToPublishArticleDto,
    IUpdateAboutAuthorDto,
    IUpdateArticleDto,
    ListSortDirection,
    PublishArticleDto,
    RedraftArticleDto,
    ScheduleToPublishArticleDto,
    UpdateAboutAuthorDto,
    UpdateArticleDto
} from "generated/article-backend";
import { IUseGenerateTokenParams } from "app/models/common/post";
import useStore, { IStore } from "app/store";
import { API, appInsightsService, StringConstants } from "app/utils/constants";
import { useMutation, useQuery } from "react-query";
import { AppQueryClient } from "./clients/query-client";

// The local environment has a trailing slash in the API_SERVER_URL, but test and prod do not.
const baseUrl : string = API.API_SERVER_URL.endsWith('/') ? API.API_SERVER_URL.slice(0,-1) : API.API_SERVER_URL;

//#region Queries
//article
export const useGetArticles = (
    pageNumber: number,
    pageSize: number,
    sortProperty: string = null,
    sortDirection: ListSortDirection = ListSortDirection.Descending,
    authorId: string = null,
    status: ArticleStatus = null,
    type: ArticleType,
    isRootTag: boolean = false,
    displayName: string = '',
    enabled: boolean = true
) => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    let rootTag : string = null;
    let tag : string = null;
    if (displayName) {
        if(isRootTag) {
            rootTag = displayName.replace('+', '%2B');
        } else {
            tag = displayName.replace('+', '%2B');
        }
    }

    return useQuery(
        ['getArticles', authorId, status, type, displayName, pageNumber],
        async () => {
            return await articleApi.article_GetArticles(null, null, status, type, enabled, undefined, authorId, tag, rootTag, null, null, sortProperty, sortDirection, pageNumber, pageSize);
        }
    );
};

//knowledge article
export const useGetKnowledgeArticles = () => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    return useQuery(
        'getKnowledgeArticles',
        async () => {
            return await articleApi.article_GetArticles(null,null,null,ArticleType.KnowledgeArticle,undefined,undefined,null,null,null,null,null,null,undefined,undefined,undefined);
        }
    );
};

export const useGetArticlesByType = (
    pageNumber: number,
    pageSize: number,
    sortProperty: string = null,
    sortDirection: ListSortDirection = ListSortDirection.Descending,
    authorId: string = null,
    status: ArticleStatus = null,
    type: ArticleType = null,
    isRootTag: boolean = false,
    displayName: string = '',
    enabled: boolean = true
) => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    let rootTag : string = null;
    let tag : string = null;
    if(displayName) {
        if(isRootTag) {
            rootTag = displayName.replace('+', '%2B'); 
        } else {
            tag = displayName.replace('+', '%2B');
        }
    }
    return useQuery(
        ['getArticlesByType', authorId, status, type, displayName, pageNumber],
        async () => {
            return await articleApi.article_GetArticlesByType(null,null,status,type,false,false,authorId,tag,rootTag,null,null,sortProperty,sortDirection,pageNumber,pageSize);
        },
        { cacheTime: 0, enabled: enabled }
    );
};

//articles In the News
export const useGetArticlesInTheNews = (
    status: ArticleStatus,
    type: ArticleType,
    filterHomePageArticles: boolean,
    pageNumber: number,
    pageSize: number,
    sortDirection: number,
    sortProperty: string
) => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    return useQuery(
        ['getArticlesInTheNews', status, type, filterHomePageArticles], async () => {
            return await articleApi.article_GetArticles(null,null,status,type,filterHomePageArticles,false,null,null,null,null,null,sortProperty,sortDirection,pageNumber,pageSize);
        }
    );
};

//banner articles
export const useGetBannerArticle = (
    status: ArticleStatus,
    filterBannerArticle: boolean,
    pageNumber: number,
    pageSize: number,
    sortDirection: number,
    sortProperty: string
) => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    return useQuery(
        ['getBannerArticle', status, filterBannerArticle], async () => {
            return await articleApi.article_GetArticles(null,null,status,null,false,filterBannerArticle,null,null,null,null,null,sortProperty,sortDirection,pageNumber,pageSize)
        }
    );
};

//Knowledge center Home page articles
export const useKnowledgeCenterHomePageArticles = () => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    return useQuery(
        ['getHomePageArticles', 'knowledgeCenter'],
        async () => {
            return await articleApi.article_GetHomeArticles(1);
        },
        { cacheTime: 0 }
    );
};

//News feed Home page articles
export const useNewsFeedHomePageArticles = () => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    return useQuery(
        ['getHomePageArticles', 'newsFeed'],
        async () => {
            return await articleApi.article_GetHomeArticles(2);
        },
        { cacheTime: 0 }
    );
};

//article templates
export const useGetArticleTemplates = () => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    return useQuery(
        'getArticleTemplates',
        async () => {
            return await articleApi.articleTemplate_GetArticleTemplates();
        },
        { enabled: false, cacheTime: 0 }
    );
};

//Get article templates
export const useGetArticleTemplateById = (templateId: string) => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    return useQuery(
        ['getArticleTemplateById', templateId],
        async () => {
            return await articleApi.articleTemplate_GetArticleTemplateById(templateId);
        },
        { enabled: templateId ? true : false }
    );
};

export const useGetAuthorById = (authorId: string) => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    return useQuery(
        ['getAuthorById', authorId],
        async () => {
            return await articleApi.author_GetAuthorById(authorId);
        }
    );
};

//Related articles
export const useGetRelatedArticles = (articleId: string) => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    return useQuery(
        ['getRelatedArticles', articleId],
        async () => {
            return await articleApi.article_GetRelatedArticlesById(articleId);
        }
    );
};

//Featured Authors
export const useGetAuthors = (sortDirection: number, pageNumber: number, pageSize: number, sortProperty?: string) => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    return useQuery('getAuthors', async () => {
        return articleApi.author_GetAuthors(sortProperty,sortDirection,pageNumber,pageSize);
    });
};

//tags
export const useGetArticleCategories = () => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    const setArticleCategories = useStore((state: IStore) => state.setArticleCategories);
    return useQuery('getArticleCategories', async () => {
        const categories : GetArticleCategoryDto[] = await articleApi.articleCategory_GetArticleCategories();
        setArticleCategories(categories);
        return categories;
    });
};

export const useGetArticle = (articleId: string) => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    return useQuery(
        ['getArticle', articleId],
        async () => {
            return await articleApi.article_GetArticleById(articleId);
        },
        { refetchOnMount: false, enabled: articleId ? true : false, cacheTime: 0 }
    );
};

// useGetPopularArticles isn't being used anywhere yet.
export const useGetPopularArticles = (status: ArticleStatus, articleType: ArticleType, enabled: boolean = true) => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    return useQuery(
        ['getPopularArticles', status, articleType],
        async () => {
            // Is enabled front page filter? Look in to what enabled means here.
            return await articleApi.article_GetArticles(null, null, status, articleType, undefined, undefined, null, null, null, null, null,"NumberOfViews", ListSortDirection.Descending, 1 , 4);
        },
        { enabled: enabled }
    );
};
// vvvv This is what's being referred to for a popular article in api-service.ts
// getPopularArticles = (
//         status: ArticleStatus,
//         articleType: ArticleType,
//         sortProperty: string = 'NumberOfViews',
//         sortDirection: number = 1,
//         pageNumber: number = 1,
//         pageSize: number = 4
//     ) =>
//         apiClient.get(
//             `/Article/List?Status=${status}&Type=${articleType}&SortProperty=${sortProperty}&SortDirection=${sortDirection}&PageNumber=${pageNumber}&PageSize=${pageSize}`
//         );

/**
 * Generates an Azure Blob Storage container SaS token to allow file uploads
 * @param params Parameters used to call the appropriate API route including the container name
 * Currently, we are only using this for Articles and Requests. Articles has been moved over to new article-api file.
 * @returns Azure Blob Storage container SaS token in the form of a URI
 */
export const useGenerateContainerSasToken = (params: IUseGenerateTokenParams) => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    const entityType = params.entity;
    return useQuery(['generateContainerSasToken', params.entity, params.containerName],
        async () => {
            if(entityType === StringConstants.ARTICLE) {
                return await articleApi.file_GenerateContainerSasToken(params.containerName);
            }
            throw new Error(`Handling not found for entity type ${entityType}`);
        }
    );
};

// get article file sas url
export const useGenerateFileSasToken = (entity: string, containerName: string, fileName: string = '') => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    return useQuery(
        ['generateFileSasToken', entity, containerName, fileName], async () => {
            if(entity === StringConstants.ARTICLE) {
                return await articleApi.file_GenerateFileSasToken(containerName, fileName);
            }
            throw new Error(`Handling not found for entity type ${entity}`);
        }
    );
};
//#endregion
//#region Mutations
//create article admin
export const useCreateArticle = () => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    const response = useMutation((data: ICreateArticleDto) => articleApi.article_CreateArticle(new CreateArticleDto(data)), {
        onSuccess: () => {
            setTimeout(() => {
                AppQueryClient.invalidateQueries('useNotificationsCount');
            }, 2000);
            appInsightsService.logEvent('useCreateArticle was Succesful');
        }
    });
    return response;
};

export const useUpdateArticle = () => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    const response = useMutation((data: IUpdateArticleDto) => articleApi.article_UpdateArticle(new UpdateArticleDto(data)), {
        onSuccess: () => {
            setTimeout(() => {
                AppQueryClient.invalidateQueries('useNotificationsCount');
            }, 2000);
            appInsightsService.logEvent('useUpdateArticle was Succesful');
        }
    });
    return response;
};

export const useUpdateAuthorDetails = () => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    const response = useMutation((data: IUpdateAboutAuthorDto) => articleApi.author_UpdateAuthor(new UpdateAboutAuthorDto(data)), {
        onSuccess: () => {
            setTimeout(() => {
                AppQueryClient.invalidateQueries('useNotificationsCount');
            }, 2000);
            appInsightsService.logEvent('useUpdateAuthorDetails was Succesful');
        }
    });
    return response;
};

export const useSubmitArticle = () => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    const response = useMutation((articleId: string) => articleApi.article_Submit(articleId), {
        onSuccess: () => {
            appInsightsService.logEvent('useSubmitArticle was Succesful');
        }
    });
    return response;
};

export const usePublishArticle = () => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    const response = useMutation(
        ({
            articleId,
            data
        }: {
            articleId: string;
            data: IPublishArticleDto
        }) => articleApi.article_Publish(articleId, new PublishArticleDto(data)),
        {
            onSuccess: () => {
                setTimeout(() => {
                    AppQueryClient.invalidateQueries('useNotificationsCount');
                }, 2000);
                appInsightsService.logEvent('usePublishArticle was Succesful');
            }
        }
    );
    return response;
};

export const useScheduleToPublishArticle = () => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    const response = useMutation(
        ({
            articleId,
            article
        }: {
            articleId: string;
            article: IScheduleToPublishArticleDto;
        }) => articleApi.article_ScheduleToPublish(articleId, new ScheduleToPublishArticleDto(article)),
        {
            onSuccess: () => {
                setTimeout(() => {
                    AppQueryClient.invalidateQueries('useNotificationsCount');
                }, 2000);
                appInsightsService.logEvent('useScheduleToPublishArticle was Succesful');
            }
        }
    );
    return response;
};

export const useReDraftArticle = () => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    const response = useMutation(
        ({ articleId, reviewNotes }: { articleId: string; reviewNotes: IRedraftArticleDto }) =>
            articleApi.article_Redraft(articleId, new RedraftArticleDto(reviewNotes)),
        {
            onSuccess: () => {
                // Query Invalidations
                // AppQueryClient.invalidateQueries('gettingRequestType');
                appInsightsService.logEvent('useReDraftArticle was Succesful');
            }
        }
    );
    return response;
};

export const useRemoveArticle = () => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    const response = useMutation((articleId: string) => articleApi.article_Remove(articleId), {
        onSuccess: () => {
            // Query Invalidations
            // AppQueryClient.invalidateQueries('gettingRequestType');
            appInsightsService.logEvent('useRemoveArticle was Succesful');
        }
    });
    return response;
};

export const useDeleteArticle = () => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    const response = useMutation((articleId: string) => articleApi.article_DeleteArticle(articleId), {
        onSuccess: () => {
            // Query Invalidations
            // AppQueryClient.invalidateQueries('gettingRequestType');
            appInsightsService.logEvent('useDeleteArticle was Succesful');
        }
    });
    return response;
};

export const usePostArticleViewed = () => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    const response = useMutation((articleId: string) => articleApi.article_TrackView(articleId), {
        onSuccess: () => {
            appInsightsService.logEvent('usePostArticleViewed was Succesful');
        },
        onError: (error) => {
            appInsightsService.logException('usePostArticleViewed failed', error as Error);
        },
    });
    return response;
};

//Article Template
export const useCreateArticleTemplate = () => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    const response = useMutation((data: ICreateArticleTemplateDto) => articleApi.articleTemplate_CreateArticleTemplate(new CreateArticleTemplateDto(data)), {
        onSuccess: () => {
            setTimeout(() => {
                AppQueryClient.invalidateQueries('useNotificationsCount');
                appInsightsService.logEvent('useCreateArticleTemplate was Succesful');
            }, 2000);
        }
    });
    return response;
};

// Delete article template
export const useDeleteArticleTemplate = () => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    const response = useMutation((articleId: string) => articleApi.articleTemplate_DeleteArticleTemplate(articleId), {
        onSuccess: () => {
            appInsightsService.logEvent('useDeleteArticleTemplate was Succesful');
        }
    });
    return response;
};

export const useDraftArticle = () => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    const response = useMutation((articleId: string) => articleApi.article_Draft(articleId), {
        onSuccess: () => {
            setTimeout(() => {
                AppQueryClient.invalidateQueries('useNotificationsCount');
            }, 2000);
            appInsightsService.logEvent('useDraftArticle was Succesful');
        }
    });
    return response;
};

//add Helpful resources
export const useAddHelpfulResource = () => {
    const articleApi : ArticleApiClient = new ArticleApiClient(baseUrl);
    const response = useMutation((data: string) => articleApi.article_GetUrlMetadata(data), {
        onSuccess: () => {
            // Query Invalidations
            appInsightsService.logEvent('useAddHelpfulResource was Succesful');
        }
    });
    return response;
};
//#endregion